import { Alert, AlertTitle, List, ListItem, type Theme, Typography } from '@mui/material'
import React, { type FC, type ReactNode } from 'react'

import { assert, type ReadonlyNonEmptyArray } from '../../../../utils'

import { ConnectwareError, ConnectwareErrorType, type NotificationSeverity, Translation } from '../../../../domain'

import { useTranslator } from '../../Internationalization'
import { IconButton } from '../Button'
import { notificationCloseIconColor } from '../../theme/alert'
import { CloseIcon } from '../../theme/icons'
import { notificationIconMapping, toAlertColor } from './Severity'

export type InlineAlertContent =
    | Readonly<{ kind: 'title', title: string }>
    | Readonly<{ kind: 'titleAndDescription', title: string, description: string }>
    | Readonly<{ kind: 'descriptionOnly', description: string }>
    | Readonly<{ kind: 'errorList', title: string, errors: ReadonlyNonEmptyArray<string> }>

export type InlineAlertProps = Readonly<{
    severity: NotificationSeverity
    content: InlineAlertContent
    /** Omit for a non-dismissible alert - there's no separate `dismissible` flag to keep in sync with it. */
    onClose?: () => void
}>

/** The error list is a step darker than a description */
const errorListStyle = (severity: NotificationSeverity) => (theme: Theme) => ({ color: theme.palette[toAlertColor[severity]][700] })

const renderContent = (severity: NotificationSeverity, content: InlineAlertContent): ReactNode => {
    switch (content.kind) {
        case 'title':
            return <AlertTitle>{content.title}</AlertTitle>
        case 'titleAndDescription':
            return (
                <>
                    <AlertTitle>{content.title}</AlertTitle>
                    <Typography variant="body2">{content.description}</Typography>
                </>
            )
        case 'descriptionOnly':
            return <Typography variant="body2">{content.description}</Typography>
        case 'errorList':
            return (
                <>
                    <AlertTitle>{content.title}</AlertTitle>
                    <List dense disablePadding sx={errorListStyle(severity)}>
                        {content.errors.map((error) => (
                            <ListItem key={error} disableGutters disablePadding>
                                <Typography variant="body2">{error}</Typography>
                            </ListItem>
                        ))}
                    </List>
                </>
            )
    }
}

const closeIconStyle = (severity: NotificationSeverity) => (theme: Theme) => {
    const color = notificationCloseIconColor[toAlertColor[severity]](theme)
    assert(color, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Theme is missing a notification close icon color', { severity }))

    return { color }
}

/**
 * In-page, never auto-dismisses - explicitly "not a system notification" per Figma. No usecase,
 * no app state: feature code calls this directly and owns whatever content/severity it needs.
 */
export const InlineAlert: FC<InlineAlertProps> = ({ severity, content, onClose }) => {
    const translator = useTranslator()

    return (
        <Alert
            data-testid="notification-inline-alert"
            data-severity={severity}
            severity={toAlertColor[severity]}
            iconMapping={notificationIconMapping}
            variant="notification"
            action={onClose && (
                <IconButton
                    data-testid="notification-inline-alert-close"
                    aria-label={translator.formatTranslation(Translation.CLOSE)}
                    variant="ghost"
                    size="small"
                    onClick={onClose}
                >
                    <CloseIcon fontSize="small" sx={closeIconStyle(severity)} />
                </IconButton>
            )}
        >
            {renderContent(severity, content)}
        </Alert>
    )
}
